import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useFormContext } from '@components/form';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconTrash } from '@components/icons/trash';
import { generateTemplateName } from '@components/integrations';
import {
  DefaultFileType,
  IntegrationEndpointArrayOfStringsVariable,
  IntegrationEndpointListVariable,
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import {
  getStringValue,
  isFieldMappedValue,
  parseDynamicOption,
  stringifyDynamicOption,
} from '@components/integrations/lists/dynamic-options-utils';
import { useDynamicEndpointOptions } from '@components/integrations/lists/use-dynamic-endpoint-options';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { useUploadFile } from 'api/files/hooks';

import { mapSavedValuesToSelectOptions } from './extract-request-data';

// IntegrationField addresses fields only by dynamic id, so its form needs no
// value shape beyond the field-mapping record.
type IntegrationFieldValues = Record<string, { value: string; variables: object } | string>;
const Form = createForm<IntegrationFieldValues>();

export const sidebarLocale = {
  en: {
    integrationError:
      'Failed to load integration, if the integration has been deleted please remove the action',
    addMore: 'Add more',
    advanced: 'Advanced',
    continueOnError: 'Continue on error',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    requestValues: 'Request values',
    oneItemPerLine: 'One item per line',
    requestHeaders: 'Request Headers',
    atLeastOneItem: 'At least one item is required',
    listLoadsOnceFilledOne: 'List loads once {fields} is filled in',
    listLoadsOnceFilledMany: 'List loads once {fields} are filled in',
    listLoadsAtRunTime: 'List loads at run time',
    selectFieldPlaceholder: 'Select {field}...',
    loadingList: 'Loading…',
  },
};

// A multi-select field's default is a list of selected option ids, unlike the
// single-string VariableValue.
export type MultiSelectFieldValue = { value: string[] };

type IntegrationFieldProps = {
  field: IntegrationEndpointVariable;
  allFields: IntegrationEndpointVariable[];
  defaultValue?: VariableValue | MultiSelectFieldValue | { [key: string]: VariableValue }[];
  savedRequestData?: Record<string, VariableValue | { [key: string]: VariableValue }[]>;
  variableGroups?: FieldMappingGroup[];
  uploadMutation?: ReturnType<typeof useUploadFile>;
  prefix?: string;
  defaultFiles?: DefaultFileType[];
  isNewAction?: boolean;
  fieldMappingId?: string;
  formStore?: FormStoreInstance;
};

export const IntegrationField = ({
  field,
  allFields,
  defaultValue: propDefaultValue,
  savedRequestData,
  variableGroups = [],
  uploadMutation,
  prefix = 'requestData',
  defaultFiles,
  isNewAction = false,
  fieldMappingId = '',
  formStore,
}: IntegrationFieldProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const variableName = generateTemplateName(allFields, field);

  const getFieldDefault = useCallback(() => {
    let fieldDefault = undefined;
    if (isNewAction) {
      if (field.type === 'file') {
        const defaultFile = (defaultFiles ?? []).find((file) => file.variableName === variableName);
        if (defaultFile) {
          fieldDefault = {
            value: defaultFile.src,
          } as VariableValue;
        }
      } else if (field.default_value) {
        fieldDefault = { value: field.default_value } as VariableValue;
      }
    }
    return fieldDefault;
  }, [defaultFiles, field, isNewAction, variableName]);

  const [defaultValue, setDefaultValue] = useState<
    VariableValue | MultiSelectFieldValue | { [key: string]: VariableValue }[] | undefined
  >(propDefaultValue ?? getFieldDefault());

  useEffect(() => {
    if (!propDefaultValue && field.type === 'array') {
      setDefaultValue([{}]);
    } else {
      setDefaultValue(propDefaultValue ?? getFieldDefault());
    }
  }, [propDefaultValue, field, getFieldDefault]);

  const isRequired = Boolean(field.required && field.visible);
  const validators: ['required'] | undefined = isRequired ? ['required'] : undefined;

  const arrayCountId = `__arrayMeta__${prefix}__${field.name}__count`;
  const initialArrayCount = useRef(
    Array.isArray(propDefaultValue) ? propDefaultValue.length : field.type === 'array' ? 1 : 0
  );

  useEffect(() => {
    if (field.type === 'array' && formStore) {
      formStore.actions.register({
        id: arrayCountId,
        defaultValue: initialArrayCount.current,
        validators: isRequired
          ? [(value) => Number(value ?? 0) >= 1 || 'atLeastOneItem']
          : undefined,
      });
      return () => formStore.actions.unregister(arrayCountId);
    }
    // eslint-disable-next-line
  }, []);

  const handleAddMoreClick = () => {
    if (Array.isArray(defaultValue)) {
      const newValue = [...defaultValue, {}];
      setDefaultValue(newValue);
      formStore?.actions.setValue(arrayCountId, newValue.length);
    } else {
      setDefaultValue([{}]);
      formStore?.actions.setValue(arrayCountId, 1);
    }
  };

  const handleRemoveClick = (index: number) => {
    if (Array.isArray(defaultValue)) {
      const updatedValue = [...defaultValue];
      updatedValue.splice(index, 1);
      setDefaultValue(updatedValue);
      formStore?.actions.setValue(arrayCountId, updatedValue.length);
    }
  };
  const defaultId = `${prefix}__${field.name}`;

  const templates = field.template ?? [];

  const renderFieldContent = () => {
    switch (field.type) {
      case 'number':
        return (
          <Form.FieldMappingNumberField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'decimal':
        return (
          <Form.FieldMappingNumberField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
            isDecimal
          />
        );
      case 'select': {
        const selectField = field as IntegrationEndpointSelectVariable;
        const hasParameterMap =
          selectField.parameter_map && Object.keys(selectField.parameter_map).length > 0;
        if (hasParameterMap && selectField.options_from) {
          return (
            <DynamicListSelectField
              id={defaultId}
              label={field.label}
              field={selectField}
              defaultValue={defaultValue as VariableValue}
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
              validators={validators}
              prefix={prefix}
              allFields={allFields}
              savedRequestData={savedRequestData}
            />
          );
        }
        return (
          <Form.FieldMappingSelectField
            id={defaultId}
            label={field.label}
            options={field.options ?? []}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      }
      case 'date':
        return (
          <Form.FieldMappingDatePicker
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'email':
        return (
          <Form.EmailField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'file':
        return (
          <Form.FieldMappingFileField
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            onSelect={uploadMutation?.mutateAsync}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'boolean':
        return (
          <Form.Switch
            // @ts-ignore What is this again? Why is id giving me an error?
            id={`${defaultId}`}
            label={field.label}
            defaultValue={Boolean((defaultValue as VariableValue)?.value) ?? false}
          />
        );
      case 'array_of_strings': {
        const arrayField = field as IntegrationEndpointArrayOfStringsVariable;
        const fieldOptions = field.options ?? [];
        const hasOptionsList = Boolean(fieldOptions.length || field.options_from);
        const rawValue = (defaultValue as VariableValue)?.value;
        const hasParameterMap =
          arrayField.parameter_map && Object.keys(arrayField.parameter_map).length > 0;
        if (hasParameterMap && arrayField.options_from) {
          return (
            <DynamicListSelectMultiField
              id={defaultId}
              label={field.label}
              field={arrayField}
              savedValues={Array.isArray(rawValue) ? (rawValue as string[]) : []}
              validators={validators}
              prefix={prefix}
              allFields={allFields}
              savedRequestData={savedRequestData}
            />
          );
        }
        if (hasOptionsList) {
          const savedValues = Array.isArray(rawValue) ? rawValue : [];
          const selectedOptions = mapSavedValuesToSelectOptions(savedValues, fieldOptions);
          return (
            <Form.SelectMulti
              // @ts-ignore dynamic requestData__<name> id; Record value type is not narrowed for SelectMulti
              id={defaultId}
              label={field.label}
              options={fieldOptions}
              defaultValue={selectedOptions}
              validators={validators}
            />
          );
        }
        const editorDefault: VariableValue = {
          value: Array.isArray(rawValue) ? rawValue.join('\n') : (rawValue as string) ?? '',
          variables: (defaultValue as VariableValue)?.variables ?? {},
        };
        return (
          <Stack space={1}>
            <Form.FieldMappingTextEditor
              id={defaultId}
              plaintext
              label={field.label}
              defaultValue={editorDefault}
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
              validators={validators}
            />
            <Text font="regular14" color="textHelper" text={t('oneItemPerLine')} />
          </Stack>
        );
      }
      case 'array':
        const arrayError =
          isRequired && formStore ? (
            <ArrayRequiredError formStore={formStore} fieldId={arrayCountId} />
          ) : null;
        const renderCard = (defaultValue: { [key in string]: VariableValue }, index: number) => (
          <Card space={2} key={`${JSON.stringify(defaultValue)}-${index}`}>
            <Stack space={2}>
              {templates.map((template) => (
                <IntegrationField
                  key={template.name}
                  field={template}
                  allFields={allFields}
                  defaultValue={defaultValue?.[template.name]}
                  variableGroups={variableGroups}
                  uploadMutation={uploadMutation}
                  prefix={`${defaultId}${index !== undefined ? `__${index}` : ''}`}
                  defaultFiles={defaultFiles}
                  isNewAction={isNewAction}
                  fieldMappingId={fieldMappingId}
                  formStore={formStore}
                />
              ))}
              <Stack orientation="horizontal" justify="flex-end">
                <Button
                  variant="secondary"
                  iconLeft={<IconTrash />}
                  dataTestid="integration-array-row-remove"
                  onClick={() => handleRemoveClick(index)}
                />
              </Stack>
            </Stack>
          </Card>
        );
        return (
          <Stack space={2}>
            <FormLabel text={asteriskLabel(field.label, isRequired)} />
            {Array.isArray(defaultValue)
              ? defaultValue.map((val, index) => renderCard(val, index))
              : renderCard({}, 0)}
            {arrayError}
            <Stack orientation="horizontal" justify="flex-end">
              <Button
                text={t('addMore')}
                variant="secondary"
                iconLeft={<IconAddPlus />}
                onClick={handleAddMoreClick}
              />
            </Stack>
          </Stack>
        );
      case 'json':
        return (
          <Form.FieldMappingJsonEditor
            id={defaultId}
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'paragraph':
        return (
          <Form.FieldMappingTextEditor
            id={defaultId}
            plaintext
            label={field.label}
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
      case 'text':
      default:
        return (
          <Form.FieldMappingTextEditor
            id={defaultId}
            plaintext
            label={field.label}
            singleLine
            defaultValue={defaultValue as VariableValue}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            validators={validators}
          />
        );
    }
  };

  if (!field.visible) {
    return <VisuallyHidden>{renderFieldContent()}</VisuallyHidden>;
  }
  return <div data-testid={`integration-field-${defaultId}`}>{renderFieldContent()}</div>;
};

const ArrayRequiredError = ({
  formStore,
  fieldId,
}: {
  formStore: FormStoreInstance;
  fieldId: string;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const error = formStore.useFieldError(fieldId);
  return <FormError text={error ? t('atLeastOneItem') : undefined} />;
};

const joinWithAnd = (items: string[], and: string): string => {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
};

type DynamicListSelectFieldProps = {
  id: string;
  label: string;
  field: IntegrationEndpointSelectVariable;
  defaultValue?: VariableValue;
  variableGroups: FieldMappingGroup[];
  fieldMappingId: string;
  validators?: ['required'];
  prefix: string;
  allFields: IntegrationEndpointVariable[];
  savedRequestData?: Record<string, VariableValue | { [key: string]: VariableValue }[]>;
};

type DynamicListFieldConfig = {
  field: IntegrationEndpointListVariable;
  label: string;
  validators?: ['required'];
  prefix: string;
  allFields: IntegrationEndpointVariable[];
  savedRequestData?: Record<string, VariableValue | { [key: string]: VariableValue }[]>;
  // Whether there's a stored value worth blocking on until the list loads.
  hasSavedValue: boolean;
};

// Shared wiring for the two dynamic-list field components: resolves the list's
// parameters from sibling fields, fetches the options, and tracks the load gate.
const useDynamicListField = ({
  field,
  label,
  validators,
  prefix,
  allFields,
  savedRequestData,
  hasSavedValue,
}: DynamicListFieldConfig) => {
  const getSavedValue = (variableName: string) => {
    const saved = savedRequestData?.[variableName];
    if (Array.isArray(saved)) return undefined;
    return getStringValue(saved);
  };
  const dynamic = useDynamicEndpointOptions(field, {
    getFieldId: (variableName) => `${prefix}__${variableName}`,
    getFieldLabel: (variableName) =>
      allFields.find((variable) => variable.name === variableName)?.label ?? variableName,
    getInitialValue: getSavedValue,
  });
  const needsOptions = hasSavedValue && dynamic.hasAllParameters;
  const [hasLoadedOptions, setHasLoadedOptions] = useState(!needsOptions || dynamic.isFetched);
  useEffect(() => {
    if (dynamic.isFetched) setHasLoadedOptions(true);
  }, [dynamic.isFetched]);
  const fieldLabel = validators?.includes('required') ? asteriskLabel(label, true) : label;
  return { dynamic, hasLoadedOptions, fieldLabel };
};

const buildListOptions = (options: { value: string; label: string }[]) =>
  options.map((option) => ({
    value: stringifyDynamicOption({ value: option.value, label: option.label }),
    label: option.label,
    endpointValue: option.value,
  }));

const DynamicListSelectField = ({
  id,
  label,
  field,
  defaultValue,
  variableGroups,
  fieldMappingId,
  validators,
  prefix,
  allFields,
  savedRequestData,
}: DynamicListSelectFieldProps) => {
  const { dynamic, hasLoadedOptions, fieldLabel } = useDynamicListField({
    field,
    label,
    validators,
    prefix,
    allFields,
    savedRequestData,
    // A field-mapped saved value needs no options to render, so don't block on them.
    hasSavedValue: Boolean(defaultValue?.value) && !isFieldMappedValue(defaultValue),
  });

  const selectOptions = useMemo(() => buildListOptions(dynamic.options), [dynamic.options]);

  if (dynamic.missingFields.length > 0) {
    return <ListLockedField label={fieldLabel} missingFields={dynamic.missingFields} />;
  }

  if (!hasLoadedOptions) {
    return <ListLoadingField label={fieldLabel} />;
  }

  return (
    <Form.FieldMappingSelectField
      id={id}
      label={label}
      options={dynamic.hasAllParameters ? selectOptions : []}
      defaultValue={defaultValue}
      variableGroups={variableGroups}
      fieldMappingId={fieldMappingId}
      validators={validators}
    />
  );
};

const ListLockedField = ({ label, missingFields }: { label: string; missingFields: string[] }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const missingLabel = joinWithAnd(missingFields, t('core.and'));
  const helpTextKey =
    missingFields.length > 1 ? 'listLoadsOnceFilledMany' : 'listLoadsOnceFilledOne';
  return (
    <TextField
      label={label}
      value=""
      placeholder={t('selectFieldPlaceholder', { field: missingLabel })}
      helpText={t(helpTextKey, { fields: missingLabel })}
      isDisabled
    />
  );
};

const ListLoadingField = ({ label }: { label: string }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <TextField
      label={label}
      value=""
      placeholder={t('loadingList')}
      isDisabled
      hideDisabledIndicator
      iconRight={<SizedRelativeSpinner size={16} $skipMargin $borderWidth={2} />}
    />
  );
};

type DynamicListSelectMultiFieldProps = {
  id: string;
  label: string;
  field: IntegrationEndpointArrayOfStringsVariable;
  savedValues: string[];
  validators?: ['required'];
  prefix: string;
  allFields: IntegrationEndpointVariable[];
  savedRequestData?: Record<string, VariableValue | { [key: string]: VariableValue }[]>;
};

// The String-array counterpart of DynamicListSelectField: fetches the dynamic
// list from its mapped parameters and offers the results as a multi-select.
const DynamicListSelectMultiField = ({
  id,
  label,
  field,
  savedValues,
  validators,
  prefix,
  allFields,
  savedRequestData,
}: DynamicListSelectMultiFieldProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { store } = useFormContext();
  const { dynamic, hasLoadedOptions, fieldLabel } = useDynamicListField({
    field,
    label,
    validators,
    prefix,
    allFields,
    savedRequestData,
    hasSavedValue: savedValues.length > 0,
  });

  // Once the list has loaded, changing a mapped parameter (e.g. the workspace)
  // clears the picks so a stale selection can't be saved against the new value.
  const previousParametersKey = useRef<string | null>(null);
  useEffect(() => {
    if (!hasLoadedOptions) return;
    if (
      previousParametersKey.current !== null &&
      previousParametersKey.current !== dynamic.parametersKey
    ) {
      store.actions.setValue(id, []);
    }
    previousParametersKey.current = dynamic.parametersKey;
  }, [dynamic.parametersKey, hasLoadedOptions, store, id]);

  const fetchedOptions = useMemo(() => buildListOptions(dynamic.options), [dynamic.options]);
  const selectedOptions = useMemo(
    () =>
      savedValues.map((value) => {
        const endpointValue = parseDynamicOption(value)?.value ?? value;
        const match = fetchedOptions.find((option) => option.endpointValue === endpointValue);
        return match
          ? { value: match.value, label: match.label }
          : { value, label: parseDynamicOption(value)?.label ?? value };
      }),
    [fetchedOptions, savedValues]
  );
  const selectOptions = useMemo(() => {
    const selected = new Set(selectedOptions.map((option) => option.value));
    const remaining = fetchedOptions
      .filter((option) => !selected.has(option.value))
      .map((option) => ({ value: option.value, label: option.label }));
    return [...selectedOptions, ...remaining];
  }, [fetchedOptions, selectedOptions]);

  const getDisabledReason = () => {
    if (dynamic.missingFields.length > 0) {
      const key =
        dynamic.missingFields.length > 1 ? 'listLoadsOnceFilledMany' : 'listLoadsOnceFilledOne';
      return t(key, { fields: joinWithAnd(dynamic.missingFields, t('core.and')) });
    }
    if (!dynamic.hasAllParameters) return t('listLoadsAtRunTime');
    if (!dynamic.isFetched) return t('loadingList');
    return undefined;
  };
  const disabledReason = getDisabledReason();

  return (
    <Form.SelectMulti
      // @ts-ignore dynamic requestData__<name> id; Record value type is not narrowed for SelectMulti
      id={id}
      label={fieldLabel}
      options={selectOptions}
      defaultValue={selectedOptions}
      validators={validators}
      isDisabled={Boolean(disabledReason)}
      placeholder={disabledReason}
    />
  );
};
